<template>
  <div>
    <div class="page-header">
      <h1 class="page-title">Check-in</h1>
      <div class="pending-counter">
        <span class="counter-num">{{ pendingCount }}</span>
        <span class="counter-label">pendente{{ pendingCount === 1 ? '' : 's' }}</span>
      </div>
    </div>

    <div class="search-box">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" class="search-icon"><path fill="currentColor" d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0c.41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5S14 7.01 14 9.5S11.99 14 9.5 14z"/></svg>
      <input class="input search-input" type="text" v-model="searchQuery" placeholder="Buscar acampante..." />
      <button v-if="searchQuery" class="clear-btn" @click="searchQuery = ''" aria-label="Limpar busca">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12z"/></svg>
      </button>
    </div>

    <SkeletonLoader v-if="store.loading && store.acampantes.length === 0" variant="table-row" :count="6" />

    <template v-else>
      <div class="checkin-list" v-if="aptos.length > 0">
        <div class="checkin-item card" v-for="item in aptos" :key="item.id">
          <div class="item-main">
            <div class="item-name-row">
              <span class="item-name">{{ item.name }}</span>
              <EquipeChip :equipe="item.equipe" />
            </div>
            <span class="item-contato" v-if="item.contato">{{ item.contato }}</span>
          </div>
          <button class="btn-checkin" @click="openCheckin(item)">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19L21 7l-1.41-1.41L9 16.17z"/></svg>
            <span>Check-in</span>
          </button>
        </div>
      </div>

      <EmptyState
        v-else-if="!searchQuery && pendingCount === 0"
        title="Todos confirmados! 🎉"
        description="Não há acampantes pendentes de check-in."
      />

      <EmptyState
        v-else-if="aptos.length === 0 && bloqueados.length === 0 && filteredAlreadyConfirmed.length === 0"
        title="Nenhum acampante encontrado"
        description="Verifique se o nome está correto ou se está inscrito."
      />

      <!-- Bloqueados por pendência financeira -->
      <div v-if="bloqueados.length > 0" class="bloq-section">
        <div class="bloq-header">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z"/></svg>
          <span>Pendência financeira ({{ bloqueados.length }})</span>
          <span class="bloq-subtitle">só passa no check-in quem está quitado</span>
        </div>
        <div class="bloq-list">
          <div class="bloq-item card" v-for="item in bloqueados" :key="item.id">
            <div class="item-main">
              <div class="item-name-row">
                <span class="item-name">{{ item.name }}</span>
                <EquipeChip :equipe="item.equipe" />
                <span class="bloq-falta">Falta {{ formatBRL(item.falta) }}</span>
              </div>
              <span class="item-contato" v-if="item.contato">{{ item.contato }}</span>
            </div>
            <div class="bloq-actions">
              <a
                v-if="whatsappLinkFor(item)"
                :href="whatsappLinkFor(item)"
                target="_blank"
                rel="noopener"
                class="btn-cobrar"
                :title="`Cobrar ${item.name} via WhatsApp`"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488a11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592c5.448 0 9.886-4.434 9.889-9.885c.002-5.462-4.415-9.89-9.881-9.892c-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.667 5.39l-.999 3.648zM12.05 5.97c-.34-.011-.66-.011-.971.062c-.32.075-.71.273-1.072.638c-.371.366-1.402 1.37-1.402 3.345s1.435 3.881 1.635 4.146c.2.264 2.79 4.267 6.844 5.978c.961.413 1.722.66 2.317.842c.985.314 1.881.27 2.591.165c.79-.117 2.448-1.002 2.79-1.97c.343-.97.343-1.797.24-1.973c-.103-.175-.376-.279-.785-.487c-.41-.207-2.42-1.197-2.795-1.334c-.376-.137-.65-.205-.923.207c-.275.412-1.06 1.334-1.299 1.609c-.24.275-.479.309-.888.103c-.41-.207-1.732-.638-3.297-2.035c-1.219-1.085-2.04-2.422-2.279-2.832c-.24-.412-.024-.635.184-.84c.184-.184.41-.479.617-.718c.207-.24.276-.412.412-.687c.137-.275.07-.514-.034-.722c-.103-.207-.918-2.214-1.293-3.026c-.349-.755-.694-.638-.923-.638z"/></svg>
                Cobrar
              </a>
              <button class="btn-secondary btn-detalhes" @click="$emit('open-details', item)" v-if="false">Ver</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Já confirmados (só aviso) -->
      <div v-if="searchQuery && filteredAlreadyConfirmed.length > 0" class="already-section">
        <div class="already-header">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="m10.6 16.6l7.05-7.05l-1.4-1.4l-5.65 5.65l-2.85-2.85l-1.4 1.4l4.25 4.25ZM12 22q-2.075 0-3.9-.788t-3.175-2.137q-1.35-1.35-2.137-3.175T2 12q0-2.075.788-3.9t2.137-3.175q1.35-1.35 3.175-2.137T12 2q2.075 0 3.9.788t3.175 2.137q1.35 1.35 2.138 3.175T22 12q0 2.075-.788 3.9t-2.137 3.175q-1.35 1.35-3.175 2.138T12 22Z"/></svg>
          <span>Já confirmado{{ filteredAlreadyConfirmed.length === 1 ? '' : 's' }}</span>
        </div>
        <div class="already-list">
          <div class="already-item" v-for="item in filteredAlreadyConfirmed" :key="item.id">
            <span class="already-name">{{ item.name }}</span>
            <EquipeChip :equipe="item.equipe" />
          </div>
        </div>
      </div>
    </template>

    <TransporteModal
      :visible="!!checkinTarget"
      :participant="checkinTarget"
      :loading="confirming"
      @select="handleSelect"
      @cancel="closeModal"
    />
  </div>
</template>

<script>
import { useAcampantesStore } from '../stores/acampantes'
import TransporteModal from '../components/TransporteModal.vue'
import EquipeChip from '../components/EquipeChip.vue'
import SkeletonLoader from '../components/ui/SkeletonLoader.vue'
import EmptyState from '../components/ui/EmptyState.vue'
import { formatBRL, statusFinanceiro, buildWhatsAppLink } from '../lib/financeiro'

function normalize(s) {
  return (s || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

export default {
  name: 'CheckInPage',
  components: { TransporteModal, EquipeChip, SkeletonLoader, EmptyState },
  data() {
    return {
      searchQuery: '',
      checkinTarget: null,
      confirming: null
    }
  },
  computed: {
    store() { return useAcampantesStore() },
    pendingCount() { return this.store.pendingCount },
    filteredPending() {
      const q = this.searchQuery.trim() ? normalize(this.searchQuery) : null
      const items = this.store.pending
      if (!q) return items
      return items.filter(a => normalize(a.name).includes(q) || normalize(a.contato).includes(q))
    },
    // Pendentes aptos pra check-in: quitados OU sem total definido.
    aptos() {
      return this.filteredPending.filter(a => statusFinanceiro(a) !== 'devendo')
    },
    // Pendentes bloqueados por dever dinheiro — exibidos numa seção avisando.
    bloqueados() {
      return this.filteredPending
        .filter(a => statusFinanceiro(a) === 'devendo')
        .slice()
        .sort((a, b) => (b.falta || 0) - (a.falta || 0))
    },
    // Aparece apenas quando há busca ativa, pra alertar o operador
    // que o acampante existe mas já fez check-in (não pode tocar/confirmar de novo).
    filteredAlreadyConfirmed() {
      if (!this.searchQuery.trim()) return []
      const q = normalize(this.searchQuery)
      return this.store.confirmed
        .filter(a => normalize(a.name).includes(q) || normalize(a.contato).includes(q))
    }
  },
  methods: {
    formatBRL,
    whatsappLinkFor(item) { return buildWhatsAppLink(item) },
    openCheckin(item) {
      this.checkinTarget = item
      this.confirming = null
    },
    closeModal() {
      if (this.confirming) return
      this.checkinTarget = null
    },
    async handleSelect(transporte) {
      if (!this.checkinTarget) return
      const target = this.checkinTarget
      this.confirming = transporte
      try {
        const res = await this.store.checkin(target.id, { transporte })
        // Sucesso real é notificado via Pusher no App.vue.
        if (res.alreadyCheckedIn) {
          this.$toast.open({
            message: `${target.name} já estava confirmado(a)`,
            type: 'warning',
            position: 'top-right',
            duration: 4000
          })
        }
        this.checkinTarget = null
      } catch (err) {
        const data = err.response?.data
        const msg = data?.message || 'Erro ao confirmar check-in'
        const isPendencia = data?.code === 'PENDENCIA_FINANCEIRA'
        this.$toast.open({
          message: msg,
          type: isPendencia ? 'warning' : 'error',
          position: 'top-right',
          duration: 5000
        })
        if (isPendencia) {
          // O acampante já tem flag updated no banco; sincroniza local.
          if (data.participant) this.store.updateFromPusher(data.participant)
          this.checkinTarget = null
        }
      } finally {
        this.confirming = null
      }
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-base);
  gap: var(--spacing-md);
}
.page-header .page-title { margin-bottom: 0; }
.pending-counter {
  display: flex;
  align-items: baseline;
  gap: var(--spacing-sm);
  padding: 6px var(--spacing-base);
  background: rgba(243, 156, 18, 0.1);
  color: var(--color-warning);
  border-radius: var(--radius-md);
  font-weight: 700;
}
.counter-num { font-size: var(--text-xl); line-height: 1; }
.counter-label { font-size: var(--text-xs); text-transform: uppercase; letter-spacing: 0.5px; }

.search-box {
  position: relative;
  max-width: 420px;
  margin-bottom: var(--spacing-base);
}
.search-input { padding-left: 38px; padding-right: 38px; }
.search-icon {
  position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
  color: var(--color-text-muted); pointer-events: none;
}
.clear-btn {
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; color: var(--color-text-muted);
  padding: 4px; display: inline-flex;
}
.clear-btn:hover { color: var(--color-danger); }

.checkin-list { display: flex; flex-direction: column; gap: var(--spacing-md); }
.checkin-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-base);
  padding: var(--spacing-base);
}
.item-main { display: flex; flex-direction: column; gap: var(--spacing-xs); min-width: 0; flex: 1; }
.item-name-row { display: flex; align-items: center; gap: var(--spacing-sm); flex-wrap: wrap; }
.item-name { font-size: var(--text-md); font-weight: 600; color: var(--color-text-primary); }
.item-contato { font-size: var(--text-sm); color: var(--color-text-muted); }

.btn-checkin {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 10px 20px;
  background: var(--color-success);
  color: #fff;
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: 700;
  cursor: pointer;
  transition: var(--transition-fast);
  box-shadow: var(--shadow-button);
  white-space: nowrap;
}
.btn-checkin:hover {
  background: #27ae60;
  box-shadow: var(--shadow-button-hover);
  transform: translateY(-1px);
}

/* Pendência financeira: lista de bloqueados */
.bloq-section {
  margin-top: var(--spacing-lg);
  padding-top: var(--spacing-base);
  border-top: 1px dashed var(--color-bg-separator);
}
.bloq-header {
  display: flex; align-items: center; gap: var(--spacing-sm); flex-wrap: wrap;
  font-size: var(--text-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--color-danger);
  margin-bottom: var(--spacing-md);
}
.bloq-subtitle {
  text-transform: none;
  font-weight: 500;
  font-size: var(--text-xs);
  letter-spacing: 0;
  color: var(--color-text-muted);
}
.bloq-list { display: flex; flex-direction: column; gap: var(--spacing-sm); }
.bloq-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-base);
  padding: var(--spacing-base);
  background: rgba(231, 76, 60, 0.04);
  border-left: 4px solid var(--color-danger);
}
.bloq-falta {
  display: inline-flex; align-items: center;
  background: rgba(231, 76, 60, 0.12);
  color: var(--color-danger);
  font-size: 10px; font-weight: 800;
  padding: 2px 8px; border-radius: 10px;
}
.bloq-actions { display: flex; gap: var(--spacing-sm); flex-shrink: 0; }
.btn-cobrar {
  display: inline-flex; align-items: center;
  gap: var(--spacing-sm);
  padding: 8px 16px;
  background: #25d366;
  color: #fff;
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--text-sm); font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  transition: var(--transition-fast);
}
.btn-cobrar:hover { background: #1ebe5d; }

/* Já confirmados (lista reduzida só de aviso) */
.already-section {
  margin-top: var(--spacing-lg);
  padding-top: var(--spacing-base);
  border-top: 1px dashed var(--color-bg-separator);
}
.already-header {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--text-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--color-success);
  margin-bottom: var(--spacing-md);
}
.already-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}
.already-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: 8px 14px;
  background: rgba(46, 204, 113, 0.06);
  border-left: 3px solid var(--color-success);
  border-radius: var(--radius-md);
  cursor: default;
}
.already-name {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-text-secondary);
  text-decoration: line-through;
  text-decoration-color: rgba(46, 204, 113, 0.4);
}

@media (max-width: 600px) {
  .checkin-item { flex-direction: column; align-items: stretch; }
  .btn-checkin { justify-content: center; }
}
</style>
